import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { fetchAdmin, putAdmin, toErrorMessage } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import {
  AdminDataTable,
  AdminNoDataState,
  AdminStatusChip,
} from '../shared';
import ConfigItemEditDialog from './ConfigItemEditDialog';
import { formatResolvedValue } from './configUtils';

const DRAWER_WIDTH = 720;
const TABS = [
  { value: 'items', label: 'Config Items' },
  { value: 'resolved', label: 'Resolved Runtime Output' },
];

function LoadingState() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={420} />
    </Stack>
  );
}

function RuntimePreviewCard({ itemKey, value }) {
  const isStructured = value !== null && typeof value === 'object';
  const isBoolean = typeof value === 'boolean';
  const isNumber = typeof value === 'number';

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            {itemKey}
          </Typography>
          {isBoolean ? (
            <Chip label={value ? 'true' : 'false'} color={value ? 'success' : 'default'} size="small" />
          ) : isNumber ? (
            <Chip label="number" color="info" size="small" variant="outlined" />
          ) : isStructured ? (
            <Chip label={Array.isArray(value) ? 'array' : 'object'} color="info" size="small" variant="outlined" />
          ) : (
            <Chip label={value === null ? 'null' : 'string'} size="small" variant="outlined" />
          )}
        </Stack>

        {isStructured ? (
          <Typography
            component="pre"
            variant="body2"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1.5,
              bgcolor: 'grey.900',
              color: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '0.84rem',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {formatResolvedValue(value)}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: value === null ? 'monospace' : undefined,
              fontWeight: isBoolean || isNumber ? 700 : 500,
            }}
          >
            {formatResolvedValue(value)}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

RuntimePreviewCard.propTypes = {
  itemKey: PropTypes.string.isRequired,
  value: PropTypes.any,
};

RuntimePreviewCard.defaultProps = {
  value: null,
};

export default function ConfigGroupDrawer({ open, groupKey, onClose, onItemUpdated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('items');
  const [state, setState] = useState({ loading: false, error: null, detail: null });
  const [selectedItem, setSelectedItem] = useState(null);
  const [submittingItem, setSubmittingItem] = useState(false);

  useEffect(() => {
    let active = true;

    if (!open || !groupKey) {
      setState({ loading: false, error: null, detail: null });
      setSelectedItem(null);
      setTab('items');
      return () => {
        active = false;
      };
    }

    setState({ loading: true, error: null, detail: null });

    const load = async () => {
      try {
        const response = await fetchAdmin(`/config/groups/${groupKey}`);

        if (!active) {
          return;
        }

        setState({ loading: false, error: null, detail: response });
      } catch (error) {
        if (!active) {
          return;
        }

        setState({ loading: false, error: toErrorMessage(error), detail: null });
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [groupKey, open]);

  const resolvedEntries = useMemo(
    () => Object.entries(state.detail?.resolved || {}),
    [state.detail]
  );

  const itemRows = useMemo(
    () =>
      (state.detail?.items || []).map((item) => ({
        id: item.id,
        original: item,
        label: item.label,
        itemKey: item.item_key,
        valueType: item.value_type,
        // value: item.is_secret ? 'Hidden secret value' : String(item.value ?? 'null'),
        // fallbackValue: item.is_secret ? 'Hidden secret fallback' : String(item.fallback_value ?? 'null'),
        active: item.is_active ? 'active' : 'inactive',
        secret: item.is_secret ? 'secret' : 'visible',
        updatedAt: item.updated_at ? fDateTime(item.updated_at) : 'Not updated',
      })),
    [state.detail]
  );

  const saveItem = async (payload) => {
    if (!selectedItem) {
      return;
    }

    setSubmittingItem(true);

    try {
      await putAdmin(`/config/items/${selectedItem.id}`, payload);
      const refreshed = await fetchAdmin(`/config/groups/${groupKey}`);
      setState({ loading: false, error: null, detail: refreshed });
      setSelectedItem(null);
      enqueueSnackbar('Config item updated', { variant: 'success' });
      if (onItemUpdated) {
        onItemUpdated(refreshed.group_key);
      }
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmittingItem(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', lg: DRAWER_WIDTH },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={1}>
              <Typography variant="h6">Config Group Workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                Inspect raw config items, understand the effective runtime output, and edit individual values without leaving the overview screen.
              </Typography>
              {groupKey ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip label={groupKey} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  {state.detail?.items?.length ? <AdminStatusChip status="active" /> : null}
                </Stack>
              ) : null}
            </Stack>
            <IconButton onClick={onClose} aria-label="Close config group drawer">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ px: 3, py: 2 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
              {TABS.map((item) => (
                <Tab key={item.value} value={item.value} label={item.label} />
              ))}
            </Tabs>
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
            {state.loading ? <LoadingState /> : null}

            {!state.loading && state.error ? (
              <Alert severity="error" variant="outlined">
                {state.error}
              </Alert>
            ) : null}

            {!state.loading && !state.error && !state.detail ? (
              <AdminNoDataState
                title="No config group loaded"
                description="Open a config group from the overview table to inspect its raw items and resolved runtime output."
              />
            ) : null}

            {!state.loading && !state.error && state.detail ? (
              <Stack spacing={3}>
                <Card variant="outlined">
                  <Box sx={{ p: 2.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="overline" color="text.secondary">
                        Group Key
                      </Typography>
                      <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
                        {state.detail.group_key}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tab === 'items'
                          ? 'Raw config items show what is stored in the database and what can be safely edited.'
                          : 'Resolved runtime output shows what the application will actually read after fallback and active overrides are merged.'}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>

                {tab === 'items' ? (
                  <AdminDataTable
                    title="Config Items"
                    description="Select an item to review its storage shape, then edit it through the typed modal flow."
                    columns={[
                      { field: 'label', headerName: 'Label' },
                      { field: 'itemKey', headerName: 'Item Key' },
                      { field: 'valueType', headerName: 'Value Type' },
                      // { field: 'value', headerName: 'Value' },
                      // { field: 'fallbackValue', headerName: 'Fallback' },
                      { field: 'active', headerName: 'Active' },
                      { field: 'secret', headerName: 'Secret' },
                      { field: 'updatedAt', headerName: 'Updated' },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        render: (row) => (
                          <Button size="small" variant="outlined" onClick={() => setSelectedItem(row.original)}>
                            Edit
                          </Button>
                        ),
                      },
                    ]}
                    rows={itemRows}
                    emptyTitle="No config items"
                    emptyDescription="The config group detail endpoint returned no raw config item rows for this group."
                  />
                ) : null}

                {tab === 'resolved' ? (
                  resolvedEntries.length ? (
                    <Stack spacing={2}>
                      {resolvedEntries.map(([itemKey, value]) => (
                        <RuntimePreviewCard key={itemKey} itemKey={itemKey} value={value} />
                      ))}
                    </Stack>
                  ) : (
                    <AdminNoDataState
                      title="No resolved runtime output"
                      description="This group detail response did not return any resolved runtime keys."
                    />
                  )
                ) : null}
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Drawer>

      <ConfigItemEditDialog
        item={selectedItem}
        open={Boolean(selectedItem)}
        submitting={submittingItem}
        onClose={() => setSelectedItem(null)}
        onSubmit={saveItem}
      />
    </>
  );
}

ConfigGroupDrawer.propTypes = {
  groupKey: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onItemUpdated: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

ConfigGroupDrawer.defaultProps = {
  groupKey: '',
  onItemUpdated: undefined,
};
